import React, { ChangeEvent, useRef, useState } from 'react';
import Icon from '@ant-design/icons';
import { Button, Modal } from 'antd';
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
  convertToPixelCrop
} from 'react-image-crop';

import { ReactComponent as PlusIconSVG } from '../../../assets/sightx-icons/svg/sx-upload.svg';
import { ReactComponent as CrossSVG } from '../../../assets/sightx-icons/svg/x-close.svg';

import 'react-image-crop/dist/ReactCrop.css';
import styles from './avatar.module.scss';

interface AvatarProps {
  initials?: string;
  imageSrc?: string;
  onChangeImage: (image: string | null) => void;
}

const centerAspectCrop = (
  mediaWidth: number,
  mediaHeight: number,
  aspect = 1
) => {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
};

export const Avatar: React.FC<AvatarProps> = ({ initials = '', imageSrc = '', onChangeImage }) => {
  const [currentImage, setCurrentImage] = useState<string | null>(imageSrc);
  const [imageForCrop, setImageForCrop] = useState<string | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const [crop, setCrop] = useState<Crop>({
    x: 0,
    y: 0,
    width: 100,
    height: 100,
    unit: '%'
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageForCrop(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = (pixelCrop: PixelCrop) => {
    if (imageForCrop) {
      setCroppedImage(getCroppedImage(imageForCrop, pixelCrop));
    }
  };

  const onCropImageFinish = () => {
    setCurrentImage(croppedImage);
    setImageForCrop(null);
    onChangeImage(croppedImage);
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const crop = centerAspectCrop(width, height);
    setCrop(crop);
    if (imageForCrop) {
      setCroppedImage(getCroppedImage(imageForCrop, convertToPixelCrop(crop, width, height)));
    }
  };

  const getCroppedImage = (imageSrc: string, pixelCrop: PixelCrop) => {
    const image = new Image();
    image.src = imageSrc;
    const renderedWidth = imageRef.current?.width ?? 1;
    const renderedHeight = imageRef.current?.height ?? 1;
    const currentWidth = image.width;
    const currentHeight = image.height;
    const widthFactor = currentWidth / renderedWidth;
    const heightFactor = currentHeight / renderedHeight;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    ctx?.drawImage(image,
      Math.trunc(pixelCrop.x * widthFactor),
      Math.trunc(pixelCrop.y * heightFactor),
      Math.trunc(pixelCrop.width * widthFactor),
      Math.trunc(pixelCrop.height * heightFactor),
      0, 0, pixelCrop.width, pixelCrop.height);
    return canvas.toDataURL('image/jpeg');
  };

  const handleModalCancel = () => {
    setImageForCrop(null);
  };

  const actions = <div className={styles.circleActions}>
    <Button
      icon={<Icon component={PlusIconSVG} />}
      onClick={handleUploadClick}
    >
      {currentImage ? 'Replace Photo' : 'Upload Photo'}
    </Button>
  </div>;

  const imageDisplayed = currentImage ? (
    <img src={currentImage} alt="profile" className={styles.circleImage} />
  ) : (
    <div className={styles.circleInitials}>{initials.toUpperCase()}</div>
  );

  return (
    <div className={styles.avatarContainer}>
      {imageDisplayed}
      {actions}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleImageChange}
        accept="image/jpeg, image/png"
      />
      <Modal
        visible={!!imageForCrop}
        onCancel={handleModalCancel}
        closeIcon={<Icon className={styles.closeIcon} component={CrossSVG} />}
        footer={[
          <Button key="back" onClick={handleModalCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={onCropImageFinish}>
            Apply
          </Button>
        ]}
      >
        <div className={styles.title}>Crop image</div>
        {!!imageForCrop && (
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={onCropComplete}
            aspect={1}
          >
            <img
              alt="Crop me"
              ref={imageRef}
              src={imageForCrop}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        )}
      </Modal>
    </div>
  );
};

